/**
 * L'écriture et l'ORDRE des noms de personnes. Un seul endroit, pour tous les
 * modules et pour les deux côtés de l'application.
 *
 * Deux règles, et elles ne disent pas la même chose :
 *
 * 1. On ÉCRIT « Prénom NOM », dans cet ordre : c'est ainsi qu'on se présente,
 *    et c'est ce que les écrans affichent depuis toujours.
 * 2. On ORDONNE par NOM DE FAMILLE, puis par prénom. Une liste de personnes se
 *    parcourt à l'œil comme un annuaire : on y cherche « Dupont », pas
 *    « Marie ». Trier la chaîne affichée revient à trier par prénom, ce qui
 *    donne un ordre que personne ne sait relire.
 *
 * Le piège est là : la chaîne composée se trie toute seule, et le résultat a
 * l'air trié. C'est ce qui s'était produit sur le calendrier d'équipe.
 *
 * Module PUR (ni base ni réseau) : le frontend l'importe tel quel, pour que
 * l'écran et le serveur ne puissent pas diverger.
 */

/** Ce qu'il faut savoir d'une personne pour l'écrire et la classer. */
export interface Personne {
  prenom?: string | null;
  nom?: string | null;
}

/** « Prénom NOM », sans espace superflu si l'un des deux manque. */
export function nomComplet(p: Personne): string {
  return `${p.prenom ?? ''} ${p.nom ?? ''}`.trim().replace(/\s+/g, ' ');
}

/**
 * Ce qu'un SCRIPT de diagnostic a le droit d'écrire dans un journal.
 *
 * Les scripts lancés en one-off Scalingo impriment dans les journaux de
 * l'application : y déverser « Prénom NOM, date d'entrée, solde » revient à
 * publier des données RH à qui peut lire les journaux. Par défaut on n'écrit
 * donc que les INITIALES — assez pour suivre une ligne d'un tableau à l'autre,
 * pas assez pour constituer un fichier.
 *
 * `nominatif` est l'exception assumée : la vérification croisée des compteurs
 * demande de savoir DE QUI on parle. Elle se demande alors explicitement, par
 * un drapeau de ligne de commande, et celui qui la demande sait ce qu'il fait.
 */
export function nomPourJournal(p: Personne, nominatif = false): string {
  if (nominatif) return nomComplet(p);
  const initiale = (v: string | null | undefined) => {
    const premiere = (v ?? '').trim().charAt(0);
    return premiere ? `${premiere.toLocaleUpperCase('fr')}.` : '';
  };
  return `${initiale(p.prenom)}${initiale(p.nom)}`.trim() || '(sans nom)';
}

/**
 * Compare deux personnes : nom de famille d'abord, prénom pour départager.
 *
 * `localeCompare` en français avec `sensitivity: 'base'` : « Élodie » se range
 * avec « Elodie », et « dupont » avec « Dupont ». Un tri SQL brut ne le fait pas
 * de la même façon selon la collation de la base — d'où le tri ici, en TypeScript,
 * partout où l'ordre est VU par quelqu'un.
 */
export function comparerParNom(a: Personne, b: Personne): number {
  const parNom = (a.nom ?? '').localeCompare(b.nom ?? '', 'fr', { sensitivity: 'base' });
  if (parNom !== 0) return parNom;
  return (a.prenom ?? '').localeCompare(b.prenom ?? '', 'fr', { sensitivity: 'base' });
}

/** Une COPIE triée : trier en place une liste rendue par une requête se voit ailleurs. */
export function trierParNom<T extends Personne>(gens: readonly T[]): T[] {
  return [...gens].sort(comparerParNom);
}

/**
 * Le même ordre, quand tout ce qu'on a est la chaîne « Prénom Nom ».
 *
 * C'est le cas des comptes applicatifs (`allowed_users.name`), qui portent un
 * seul champ libre. Convention retenue : le PREMIER mot est le prénom, tout le
 * reste est le nom — « Jean Le Goff » se range à « Le Goff ». Un prénom composé
 * non tirété la met en défaut, et c'est assumé : ces comptes ont vocation à être
 * rattachés à une fiche salarié, qui porte les deux champs séparément.
 */
export function personneDepuisNomEcrit(nomEcrit: string | null | undefined): Personne {
  const mots = (nomEcrit ?? '').trim().split(/\s+/).filter(Boolean);
  if (mots.length === 0) return { prenom: '', nom: '' };
  if (mots.length === 1) return { prenom: '', nom: mots[0]! };
  return { prenom: mots[0]!, nom: mots.slice(1).join(' ') };
}

/** Compare deux « Prénom Nom » écrits d'un bloc. Voir `personneDepuisNomEcrit`. */
export function comparerParNomEcrit(a: string | null | undefined, b: string | null | undefined): number {
  return comparerParNom(personneDepuisNomEcrit(a), personneDepuisNomEcrit(b));
}
